import React, { useCallback, useMemo } from 'react';
import { isEmpty } from 'lodash';

import { SortableTable, Text } from '~/components/core';
import Grid from '~/components/core/Atomic/Grid/Grid';
import CounterWithRefresh from '~/components/core/CounterWithRefresh/CounterWithRefresh';
import SkeletonTable from '~/components/core/Skeletons/SkeletonTable';
import type { PaginationProps } from '~/components/core/Tables/SortableTable/TablePaginationSection/types';
import ThreeDotsMenu from '~/components/core/ThreeDotsMenu';
import type { PaginationState } from '~/hooks/useIdBasedPagination';
import type { WorkflowExecutionModel } from '~/server_shared/openapi/data-contracts';

import ExecutionStatusBadge from './ExecutionStatusBadge';

interface WorkflowExecutionsTableProps {
  executions: WorkflowExecutionModel[];
  loading: boolean;
  pagination: PaginationState;
  onViewDetails: (execution: WorkflowExecutionModel) => void;
  onRefresh: () => Promise<void>;
  onSortChange?: (columnId: string, direction: 'asc' | 'desc') => void;
}

export const WorkflowExecutionsTable: React.FC<WorkflowExecutionsTableProps> = ({
  executions,
  loading,
  pagination,
  onViewDetails,
  onRefresh,
  onSortChange,
}) => {
  const calculateRunTime = useCallback((execution: WorkflowExecutionModel): string => {
    // Only calculate run time if we have started_datetime (when engine actually started processing)
    if (!execution.started_datetime) return 'N/A';

    const startTime = new Date(execution.started_datetime);
    const endTime = execution.completed_datetime ? new Date(execution.completed_datetime) : new Date();
    const diffMs = endTime.getTime() - startTime.getTime();

    if (diffMs < 1000) return '< 1s';
    if (diffMs < 60000) return `${Math.floor(diffMs / 1000)}s`;
    if (diffMs < 3600000) return `${Math.floor(diffMs / 60000)}m`;
    return `${Math.floor(diffMs / 3600000)}h`;
  }, []);

  const tableColumns = useMemo(
    () => [
      {
        id: 'workflow_name',
        label: 'Workflow Name',
        disableSort: true,
        specialCell: (execution: WorkflowExecutionModel) => execution.workflow_name || 'N/A',
      },
      {
        id: 'rule_display_name',
        label: 'Rule Name',
        disableSort: true,
        specialCell: (execution: WorkflowExecutionModel) => execution.rule_display_name || 'N/A',
      },
      {
        id: 'status',
        label: 'Status',
        disableSort: true,
        specialCell: (execution: WorkflowExecutionModel) => <ExecutionStatusBadge status={execution.status} />,
      },
      {
        id: 'submitted_datetime',
        label: 'Submitted',
        disableSort: false,
        specialCell: (execution: WorkflowExecutionModel) =>
          execution.submitted_datetime ? new Date(execution.submitted_datetime).toLocaleDateString() : 'N/A',
      },
      {
        id: 'run_time',
        label: 'Run Time',
        disableSort: true,
        specialCell: (execution: WorkflowExecutionModel) => calculateRunTime(execution),
      },
      {
        id: 'actions',
        label: 'Actions',
        disableSort: true,
        specialCell: (execution: WorkflowExecutionModel) => (
          <div>
            <ThreeDotsMenu
              options={[
                {
                  key: 'view-details',
                  node: 'View Details',
                  onClick: () => onViewDetails(execution),
                },
              ]}
            />
          </div>
        ),
      },
    ],
    [calculateRunTime, onViewDetails]
  );

  const paginationProps: PaginationProps = useMemo(
    () => ({
      page: pagination.page,
      count: pagination.totalCount,
      rowsPerPage: pagination.rowsPerPage,
      onChangePage: (_event: React.MouseEvent<HTMLButtonElement> | null, page: number) => {
        pagination.setPage(page);
      },
      onChangeRowsPerPage: (event: React.ChangeEvent<HTMLInputElement>) => {
        pagination.setRowsPerPage(parseInt(event.target.value, 10));
      },
      rowsPerPageOptions: [10, 25, 50, 100],
    }),
    [pagination]
  );

  const counterViewText = (
    <CounterWithRefresh
      onRefresh={onRefresh}
      isLoading={loading}
      pagination={pagination}
      refreshTooltipText="Refresh workflow executions"
      refreshAriaLabel="Refresh workflow executions"
    />
  );

  const handleSortByColumn = useCallback(
    (columnId: string, direction: 'asc' | 'desc') => {
      // Only allow sorting by submitted_datetime in the frontend
      if (columnId === 'submitted_datetime' && onSortChange) {
        onSortChange(columnId, direction);
      }
      // For other columns, ignore the sort request since we only support submitted_datetime
    },
    [onSortChange]
  );

  return (
    <Grid item xs={12}>
      {loading ? (
        <SkeletonTable rowsCount={5} columnsCount={6} />
      ) : (
        <SortableTable
          rows={executions}
          columns={tableColumns}
          stickyHeader
          paginationProps={paginationProps}
          onSortByColumn={handleSortByColumn}
          emptyStateComponent={
            <div className="py-8 text-center">
              <Text weight="medium" variant="large">
                {isEmpty(executions) && !loading
                  ? "We couldn't find any workflow executions which match your current search, try refining your search"
                  : 'No workflow executions found'}
              </Text>
            </div>
          }
          counterViewText={counterViewText}
        />
      )}

      {pagination.resultsTruncated && (
        <div className="warning text-orange-600 p-2">
          Results limited to {pagination.totalCount}. Refine filters for complete results.
        </div>
      )}
    </Grid>
  );
};
